import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { api } from '../api/client';
import { downloadCsv } from '../lib/download';
import { useApiResource } from '../lib/useApiResource';
import {
  Button,
  Card,
  ErrorRetry,
  Field,
  Input,
  Money,
  Textarea,
  useToast,
  NumberInput,
} from '../ui';
import { CsvImportModal } from './CsvImportModal';

interface Customer {
  id: number;
  code: string;
  name: string;
  econ_no: string;
  national_id: string;
  phone: string;
  email: string;
  address: string;
  credit_limit: number;
  terms: string;
  notes: string;
  version: number;
}

interface FormState {
  id: number | null;
  version: number | null;
  code: string;
  name: string;
  econ_no: string;
  national_id: string;
  phone: string;
  email: string;
  address: string;
  credit_limit: string;
  terms: string;
  notes: string;
}

const EMPTY: FormState = {
  id: null,
  version: null,
  code: '',
  name: '',
  econ_no: '',
  national_id: '',
  phone: '',
  email: '',
  address: '',
  credit_limit: '',
  terms: '',
  notes: '',
};

type StrField = Exclude<keyof FormState, 'id' | 'version'>;

export function CustomersScreen({
  openCustomerId = null,
  onCustomerOpened,
}: {
  /** open a specific customer for editing (e.g. deep-linked from the dashboard's over-credit-limit list). */
  openCustomerId?: number | null;
  onCustomerOpened?: () => void;
} = {}) {
  const [importOpen, setImportOpen] = useState(false);
  const toast = useToast();
  const { data, status, error: loadError, reload } = useApiResource<Customer[]>('/api/customers');
  const list = data ?? [];
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const edit = useCallback((c: Customer) => {
    setForm({
      id: c.id,
      version: c.version,
      code: c.code,
      name: c.name,
      econ_no: c.econ_no,
      national_id: c.national_id,
      phone: c.phone,
      email: c.email,
      address: c.address,
      credit_limit: c.credit_limit ? String(c.credit_limit) : '',
      terms: c.terms,
      notes: c.notes,
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Deep-link focus (E16-35): open the requested customer once its row has loaded, so the dashboard's
  // «فراتر از سقف اعتبار» list is a live link to the customer record (where the limit/terms are set) —
  // not a dead-end count. The ref makes it fire once per requested id.
  const focusedRef = useRef<number | null>(null);
  useEffect(() => {
    if (openCustomerId == null || focusedRef.current === openCustomerId) return;
    const c = list.find((x) => x.id === openCustomerId);
    if (c) {
      focusedRef.current = openCustomerId;
      edit(c);
      onCustomerOpened?.();
    }
  }, [openCustomerId, list, edit, onCustomerOpened]);

  const set =
    (k: StrField) => (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      code: form.code.trim(),
      econ_no: form.econ_no.trim(),
      national_id: form.national_id.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      terms: form.terms.trim(),
      notes: form.notes.trim(),
    };
    const cl = form.credit_limit.trim();
    if (cl) payload.credit_limit = Number(cl);
    if (form.id != null) {
      payload.id = form.id;
      payload.version = form.version;
    }
    const res = await api<Customer>('/api/customers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setBusy(false);

    if (res.status === 409) {
      toast('این مشتری همین لحظه تغییر کرد — نسخهٔ به‌روز بارگذاری شد.', 'error');
      const current = (res.body as { current?: Customer }).current;
      if (current) edit(current);
      reload();
      return;
    }
    if (!res.ok) {
      setError((res.body as { message?: string }).message ?? 'خطا در ذخیره');
      return;
    }
    toast('ذخیره شد');
    setForm(EMPTY);
    reload();
  }

  function exportCsv() {
    downloadCsv('customers', [
      ['نام', 'کد', 'شمارهٔ اقتصادی', 'کد ملی', 'تلفن', 'ایمیل', 'آدرس', 'سقف اعتبار', 'شرایط'],
      ...list.map((c) => [
        c.name,
        c.code,
        c.econ_no,
        c.national_id,
        c.phone,
        c.email,
        c.address,
        c.credit_limit,
        c.terms,
      ]),
    ]);
  }

  const editing = form.id != null;

  return (
    <>
      <Card title={editing ? `ویرایش مشتری: ${form.name}` : 'افزودن مشتری'}>
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="نام مشتری" htmlFor="c_name">
              <Input id="c_name" value={form.name} onChange={set('name')} autoFocus />
            </Field>
            <Field label="کد مشتری">
              <Input value={form.code} onChange={set('code')} />
            </Field>
            <Field label="شمارهٔ اقتصادی">
              <Input value={form.econ_no} onChange={set('econ_no')} />
            </Field>
            <Field label="شناسه / کد ملی">
              <Input value={form.national_id} onChange={set('national_id')} />
            </Field>
            <Field label="تلفن">
              <Input value={form.phone} onChange={set('phone')} />
            </Field>
            <Field label="ایمیل">
              <Input value={form.email} onChange={set('email')} />
            </Field>
            <Field label="سقف اعتبار (﷼)">
              <NumberInput
                value={form.credit_limit}
                onValue={(n) => setForm((f) => ({ ...f, credit_limit: n ? String(n) : '' }))}
              />
            </Field>
            <Field label="شرایط پیش‌فرض">
              <Input value={form.terms} onChange={set('terms')} />
            </Field>
          </div>
          <Field label="آدرس">
            <Textarea rows={2} value={form.address} onChange={set('address')} />
          </Field>
          <Field label="یادداشت">
            <Textarea rows={2} value={form.notes} onChange={set('notes')} />
          </Field>
          {error ? <div className="field-error">{error}</div> : null}
          <div className="row" style={{ marginTop: 12 }}>
            <Button type="submit" loading={busy}>
              {editing ? 'ذخیرهٔ تغییرات' : 'افزودن مشتری'}
            </Button>
            {editing ? (
              <Button type="button" variant="secondary" onClick={() => setForm(EMPTY)}>
                جدید
              </Button>
            ) : null}
          </div>
        </form>
      </Card>

      <Card title={`مشتریان (${list.length})`}>
        <div className="row" style={{ marginBottom: 10 }}>
          <Button variant="secondary" onClick={exportCsv} disabled={list.length === 0}>
            خروجی CSV
          </Button>
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            ⬆ ورود گروهی (CSV)
          </Button>
        </div>
        {importOpen ? (
          <CsvImportModal
            title="ورود گروهی مشتریان (CSV)"
            hint="ستون‌ها: نام (لازم)، کد، شماره اقتصادی، شناسه ملی، تلفن، ایمیل، نشانی، سقف اعتبار، شرایط، یادداشت"
            previewUrl="/api/customers/import/preview"
            commitUrl="/api/customers/import"
            onClose={() => setImportOpen(false)}
            onDone={() => {
              setImportOpen(false);
              reload();
            }}
          />
        ) : null}
        {status === 'error' ? (
          <ErrorRetry error={loadError} onRetry={() => reload()} />
        ) : status === 'loading' ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>شمارهٔ اقتصادی</th>
                  <th>تلفن</th>
                  <th>سقف اعتبار</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={5}>
                      هنوز مشتری‌ای ثبت نشده — از فرم بالا اضافه کنید.
                    </td>
                  </tr>
                ) : (
                  list.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.name}
                        <div className="muted">{c.code}</div>
                      </td>
                      <td>{c.econ_no || '—'}</td>
                      <td>{c.phone || '—'}</td>
                      <td>
                        <Money value={c.credit_limit} />
                      </td>
                      <td>
                        <Button variant="ghost" onClick={() => edit(c)}>
                          ویرایش
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
